import { X, Mic, Cpu, FileCheck, AlertTriangle, Download, BookOpen, ArrowRight, Zap, Brain, Shield, LayoutTemplate } from 'lucide-react';

interface GuideModalProps {
  onClose: () => void;
}

function SectionHeading({ icon: Icon, title, color }: { icon: any; title: string; color: string }) {
  return (
    <div className="flex items-center gap-3 mb-4 mt-10 first:mt-0">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    </div>
  );
}

function StepCard({ number, title, description }: { number: number; title: string; description: string }) {
  return (
    <div className="flex gap-4 items-start">
      <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5">
        {number}
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
        <p className="text-sm text-gray-600 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function FlowDiagram() {
  const steps = [
    { label: 'Dictate', sublabel: 'Voice / Text', color: 'bg-blue-500', icon: Mic },
    { label: 'Classify', sublabel: 'Auto-detect exam', color: 'bg-violet-500', icon: Cpu },
    { label: 'Generate', sublabel: 'AI expansion', color: 'bg-emerald-500', icon: Brain },
    { label: 'Validate', sublabel: 'Issue detection', color: 'bg-amber-500', icon: Shield },
    { label: 'Review', sublabel: 'Edit & sign', color: 'bg-primary-600', icon: FileCheck },
    { label: 'Export', sublabel: 'RTF / HL7 / Text', color: 'bg-rose-500', icon: Download },
  ];

  return (
    <div className="bg-gray-50 rounded-xl p-6 my-6">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-5 text-center">Report Lifecycle</h3>
      <div className="flex items-center justify-between gap-1">
        {steps.map((step, i) => (
          <div key={step.label} className="flex items-center gap-1 flex-1">
            <div className="flex flex-col items-center flex-1">
              <div className={`w-11 h-11 rounded-xl ${step.color} flex items-center justify-center shadow-sm`}>
                <step.icon size={18} className="text-white" />
              </div>
              <span className="text-xs font-semibold text-gray-800 mt-2">{step.label}</span>
              <span className="text-[10px] text-gray-500">{step.sublabel}</span>
            </div>
            {i < steps.length - 1 && (
              <ArrowRight size={14} className="text-gray-300 flex-shrink-0 -mt-4" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ClassificationDiagram() {
  const tiers = [
    { label: 'Deterministic', desc: 'Regex pattern matching on exam text', speed: 'Instant', cost: 'Free', confidence: '0.85-0.95', color: 'border-emerald-400 bg-emerald-50' },
    { label: 'ML Classifier', desc: 'Per-user sklearn model trained on history', speed: 'Instant', cost: 'Free', confidence: '0.70+', color: 'border-blue-400 bg-blue-50' },
    { label: 'LLM Classifier', desc: 'Claude Opus 4.6 with structured output', speed: '1-2s', cost: 'API call', confidence: '0.90+', color: 'border-violet-400 bg-violet-50' },
    { label: 'Fallback', desc: 'Generic template based on modality', speed: 'Instant', cost: 'Free', confidence: 'N/A', color: 'border-gray-400 bg-gray-50' },
  ];

  return (
    <div className="my-6">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Classification Cascade</h3>
      <div className="space-y-2">
        {tiers.map((tier, i) => (
          <div key={tier.label} className="flex items-start gap-3">
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={`w-6 h-6 rounded-full border-2 ${tier.color} flex items-center justify-center text-xs font-bold text-gray-700`}>
                {i + 1}
              </div>
              {i < tiers.length - 1 && <div className="w-0.5 h-6 bg-gray-200 mt-1" />}
            </div>
            <div className={`flex-1 rounded-lg border-2 px-4 py-2.5 ${tier.color}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm text-gray-800">{tier.label}</span>
                <div className="flex gap-3 text-[10px] text-gray-500">
                  <span>{tier.speed}</span>
                  <span>{tier.cost}</span>
                  <span>Confidence: {tier.confidence}</span>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-0.5">{tier.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3 italic">Each tier is tried in order. The first tier to return a high-confidence result wins. Most common exams are classified deterministically without any API call.</p>
    </div>
  );
}

function ValidationDiagram() {
  const checks = [
    { category: 'Laterality', example: 'MRI knee must specify left or right', severity: 'error', color: 'bg-red-100 text-red-700' },
    { category: 'Measurements', example: 'Pulmonary nodule must have size in mm', severity: 'warning', color: 'bg-amber-100 text-amber-700' },
    { category: 'Required Sections', example: 'Mammography must include BI-RADS', severity: 'error', color: 'bg-red-100 text-red-700' },
    { category: 'Critical Findings', example: 'Acute haemorrhage flagged as urgent', severity: 'critical', color: 'bg-rose-100 text-rose-700' },
    { category: 'Consistency', example: 'Report body matches Conclusion', severity: 'warning', color: 'bg-amber-100 text-amber-700' },
    { category: 'Completeness', example: 'Missing comparison studies', severity: 'info', color: 'bg-blue-100 text-blue-700' },
  ];

  return (
    <div className="my-6">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Validation Checks</h3>
      <div className="grid grid-cols-2 gap-2">
        {checks.map((check) => (
          <div key={check.category} className="rounded-lg border border-gray-200 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm text-gray-800">{check.category}</span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${check.color}`}>{check.severity}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{check.example}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExamTypeGrid() {
  const exams = [
    { modality: 'CT', types: ['Head', 'Chest', 'Abdomen/Pelvis', 'Angiography (CTA/CTPA)', 'Cervical Spine'], color: 'bg-blue-100 text-blue-700' },
    { modality: 'MRI', types: ['Brain', 'Knee', 'Lumbar Spine', 'Cervical Spine', 'Shoulder'], color: 'bg-violet-100 text-violet-700' },
    { modality: 'X-Ray', types: ['Chest', 'Abdomen', 'Musculoskeletal'], color: 'bg-emerald-100 text-emerald-700' },
    { modality: 'Ultrasound', types: ['Abdomen', 'Thyroid', 'Renal', 'Pelvic'], color: 'bg-amber-100 text-amber-700' },
    { modality: 'Mammography', types: ['Screening', 'Diagnostic'], color: 'bg-rose-100 text-rose-700' },
    { modality: 'Nuclear Med', types: ['Bone Scan', 'V/Q Scan'], color: 'bg-cyan-100 text-cyan-700' },
  ];

  return (
    <div className="my-6">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Supported Exam Types (31 total)</h3>
      <div className="grid grid-cols-3 gap-3">
        {exams.map((exam) => (
          <div key={exam.modality} className="rounded-lg border border-gray-200 p-3">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${exam.color}`}>{exam.modality}</span>
            <ul className="mt-2 space-y-1">
              {exam.types.map((t) => (
                <li key={t} className="text-xs text-gray-600">- {t}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GuideModal({ onClose }: GuideModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
              <BookOpen size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">RadiologyDashboard Guide</h1>
              <p className="text-xs text-gray-500">AI-Powered Radiology Reporting Engine</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Overview */}
          <SectionHeading icon={Zap} title="How It Works" color="bg-primary-600" />
          <p className="text-sm text-gray-600 leading-relaxed">
            RadiologyDashboard transforms radiologist dictations into structured, clinically accurate reports using AI.
            You speak or type shorthand findings, and the engine expands them into a complete report with proper
            sections, normal-filling, and terminology — following Australian/NZ radiology conventions.
          </p>

          <FlowDiagram />

          {/* Step by step */}
          <SectionHeading icon={Mic} title="Step-by-Step Workflow" color="bg-blue-500" />

          <div className="space-y-5 my-4">
            <StepCard
              number={1}
              title="Create a New Report"
              description="Click 'New Report' in the left panel. Enter the patient identifier and study date. The modality and body part will be auto-detected from your dictation."
            />
            <StepCard
              number={2}
              title="Dictate Your Findings"
              description="Use the microphone button (push-to-talk) or type directly into the dictation box. Use your normal radiology shorthand — abbreviations like 'NAD', 'no hge', 'normal GW diff' are understood. Include the exam type in your first sentence (e.g., 'CT head non-contrast. 45F fall...')."
            />
            <StepCard
              number={3}
              title="Select a Template (Optional)"
              description="Click the template button to open the Template Browser. Templates are auto-detected based on your exam type, but you can manually override. Each template defines the report structure, required sections, and expansion rules specific to that exam type."
            />
            <StepCard
              number={4}
              title="Generate the Report"
              description="Click 'Generate'. The engine classifies your exam type, selects the appropriate template, and uses Claude Opus 4.6 to expand your shorthand into a structured report. Normal findings are automatically filled in for structures you didn't mention, following the exam-specific template."
            />
            <StepCard
              number={5}
              title="Review Issues"
              description="The right panel shows validation issues found in the generated report. Issues are exam-specific: laterality checks for MRI knee, measurement requirements for nodules, BI-RADS for mammography, critical finding flags for emergencies. Click an issue to jump to the relevant section."
            />
            <StepCard
              number={6}
              title="Edit and Finalise"
              description="The report opens in the rich text editor. Edit any section directly — fix AI suggestions, add findings, adjust wording. Use the toolbar to format text. You can regenerate individual sections if needed without losing your edits elsewhere."
            />
            <StepCard
              number={7}
              title="Sign and Export"
              description="When satisfied, click 'Sign' to finalise the report. Export in your preferred format: RTF (for Word/RIS), HL7 (for PACS integration), plain text, or clipboard. The export includes all metadata: patient ID, study date, modality, and report content."
            />
          </div>

          {/* Classification Engine */}
          <SectionHeading icon={Cpu} title="Classification Engine" color="bg-violet-500" />
          <p className="text-sm text-gray-600 leading-relaxed">
            When you dictate, the engine automatically identifies the exam type using a multi-tier classification cascade.
            This determines which report template, section structure, normal findings, and validation rules to apply.
            The system is designed to be fast and cost-effective — most classifications happen instantly without an API call.
          </p>

          <ClassificationDiagram />

          <div className="bg-violet-50 rounded-lg p-4 my-4 border border-violet-200">
            <h4 className="text-sm font-semibold text-violet-800">How Classification Improves Over Time</h4>
            <p className="text-xs text-violet-600 mt-1">
              Every time you accept or override a template, the system records your preference. After enough signals,
              a personalised ML classifier is trained on your patterns — learning which templates you prefer for
              ambiguous cases. This means the system gets more accurate the more you use it.
            </p>
          </div>

          {/* AI Report Generation */}
          <SectionHeading icon={Brain} title="AI Report Generation" color="bg-emerald-500" />
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            The report generation engine uses Claude Opus 4.6 with domain-specific prompts tailored to each exam type.
            The AI receives three layers of guidance:
          </p>

          <div className="space-y-3 my-4">
            <div className="rounded-lg border-l-4 border-emerald-400 bg-emerald-50 p-4">
              <h4 className="text-sm font-semibold text-emerald-800">1. Report Structure</h4>
              <p className="text-xs text-emerald-700 mt-1">
                Each exam type defines specific sections and anatomical subsections. For example, CT Head
                requires: brain parenchyma, ventricles/CSF, extra-axial spaces, calvarium/skull base,
                paranasal sinuses, orbits, and soft tissues. The AI follows this structure exactly.
              </p>
            </div>
            <div className="rounded-lg border-l-4 border-blue-400 bg-blue-50 p-4">
              <h4 className="text-sm font-semibold text-blue-800">2. Normal Findings Reference</h4>
              <p className="text-xs text-blue-700 mt-1">
                The AI receives a complete normal report template for the exam type. When you don't mention
                a structure, the AI fills in the appropriate normal finding (e.g., "The visualised paranasal
                sinuses are clear"). This is called "normal-filling" and follows standard radiology practice.
              </p>
            </div>
            <div className="rounded-lg border-l-4 border-amber-400 bg-amber-50 p-4">
              <h4 className="text-sm font-semibold text-amber-800">3. Pathology Pattern Matching</h4>
              <p className="text-xs text-amber-700 mt-1">
                When your dictation mentions pathology (e.g., "stroke", "PE", "appendicitis"), the engine
                matches it to known patterns and provides expansion guidance — including key descriptors,
                measurement requirements, and relevant clinical guidelines (e.g., Fleischner criteria for
                lung nodules, PIOPED for V/Q scans).
              </p>
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-4 my-4 border border-red-200">
            <h4 className="text-sm font-semibold text-red-800 flex items-center gap-2">
              <AlertTriangle size={14} />
              Safety Constraints
            </h4>
            <ul className="text-xs text-red-700 mt-2 space-y-1">
              <li>- The AI will <strong>never invent abnormal findings</strong> that you didn't dictate</li>
              <li>- It will <strong>never fabricate measurements, laterality, or technique details</strong></li>
              <li>- If your findings are inconsistent, it preserves them and adds a "clarification required" note</li>
              <li>- The AI maintains consistency between the Report body and Conclusion</li>
              <li>- All generated reports require radiologist review and sign-off before export</li>
            </ul>
          </div>

          {/* Validation */}
          <SectionHeading icon={Shield} title="Validation & Quality Checks" color="bg-amber-500" />
          <p className="text-sm text-gray-600 leading-relaxed">
            After report generation, two layers of validation run automatically:
            <strong> regex-based checks</strong> (instant, deterministic) and
            <strong> LLM-based review</strong> (deeper semantic analysis). Issues appear in the right sidebar,
            colour-coded by severity. Validation rules are specific to each exam type.
          </p>

          <ValidationDiagram />

          {/* Templates */}
          <SectionHeading icon={LayoutTemplate} title="Template System" color="bg-indigo-500" />
          <p className="text-sm text-gray-600 leading-relaxed">
            Templates define how reports are structured for each exam type. The system comes with 18 pre-built
            templates covering all major modalities. You can also create your own or clone and customise existing ones.
          </p>

          <ExamTypeGrid />

          <div className="bg-indigo-50 rounded-lg p-4 my-4 border border-indigo-200">
            <h4 className="text-sm font-semibold text-indigo-800">Template Features</h4>
            <ul className="text-xs text-indigo-700 mt-2 space-y-1">
              <li><strong>Auto-detection:</strong> The correct template is selected automatically based on your dictation</li>
              <li><strong>Manual override:</strong> Use the Template Browser to select a different template</li>
              <li><strong>Clone & customise:</strong> Clone any shared template to create your own version</li>
              <li><strong>Set defaults:</strong> Mark your preferred template as default for each exam type</li>
              <li><strong>Version tracking:</strong> Templates are versioned so you can iterate on your preferred style</li>
            </ul>
          </div>

          {/* Export */}
          <SectionHeading icon={Download} title="Export Formats" color="bg-rose-500" />
          <div className="grid grid-cols-2 gap-3 my-4">
            <div className="rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-sm text-gray-800">RTF (Rich Text)</h4>
              <p className="text-xs text-gray-500 mt-1">For pasting into Word, RIS systems, or email. Preserves formatting, headings, and structure.</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-sm text-gray-800">HL7 FHIR</h4>
              <p className="text-xs text-gray-500 mt-1">For PACS/RIS integration. Includes diagnostic report resource with patient reference, study metadata, and structured content.</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-sm text-gray-800">Plain Text</h4>
              <p className="text-xs text-gray-500 mt-1">Clean text with section headings. Suitable for systems that don't support rich formatting.</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-sm text-gray-800">Clipboard</h4>
              <p className="text-xs text-gray-500 mt-1">One-click copy to clipboard. Stripped formatting for quick paste into any application.</p>
            </div>
          </div>

          {/* Tips */}
          <SectionHeading icon={Zap} title="Tips for Best Results" color="bg-teal-500" />
          <div className="space-y-3 my-4">
            <div className="flex gap-3 items-start bg-gray-50 rounded-lg p-3">
              <span className="text-lg">1.</span>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Start with the exam type</h4>
                <p className="text-xs text-gray-600">Begin your dictation with "CT head...", "MRI knee...", "Chest X-ray..." so the classifier can identify the exam immediately.</p>
              </div>
            </div>
            <div className="flex gap-3 items-start bg-gray-50 rounded-lg p-3">
              <span className="text-lg">2.</span>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Include clinical history</h4>
                <p className="text-xs text-gray-600">Mention the patient's age, gender, and reason for the study. This populates the Clinical History section and helps the AI contextualise findings.</p>
              </div>
            </div>
            <div className="flex gap-3 items-start bg-gray-50 rounded-lg p-3">
              <span className="text-lg">3.</span>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Focus on abnormal findings</h4>
                <p className="text-xs text-gray-600">You don't need to dictate every normal structure. The AI automatically fills in normal findings for structures you don't mention. Focus your dictation on what's abnormal.</p>
              </div>
            </div>
            <div className="flex gap-3 items-start bg-gray-50 rounded-lg p-3">
              <span className="text-lg">4.</span>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Use shorthand freely</h4>
                <p className="text-xs text-gray-600">Common abbreviations are understood: "NAD", "no hge", "GW diff", "bilat", "abdo", "CTPA". The engine expands these into proper terminology.</p>
              </div>
            </div>
            <div className="flex gap-3 items-start bg-gray-50 rounded-lg p-3">
              <span className="text-lg">5.</span>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Always review before signing</h4>
                <p className="text-xs text-gray-600">AI-generated reports require your clinical judgement. Review the full report, check the validation issues, and ensure accuracy before signing off.</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">
              RadiologyDashboard by Axiom Data | Powered by Claude Opus 4.6
            </p>
            <p className="text-xs text-gray-400 mt-1">
              This tool assists radiologists. All reports require clinical review and sign-off.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
